import { Play, Square, Timer } from 'lucide-react';
import * as React from 'react';
import { formatDuration } from '@cfl/shared';

import {
  Button,
  Combobox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { fitsProject, useActivities, useProjects } from '@/lib/catalog-api';
import { useRunningEntry, useStartEntry, useStopEntry } from '@/lib/timesheet-api';

/**
 * The clock, in the top bar.
 *
 * A running entry - no `end`, no `durationSeconds` - is a shape the schema has
 * always allowed and this app has always been able to RENDER, on three screens.
 * Nothing could create one, so "running" was a state that could be read and never
 * reached. This is the control that reaches it.
 *
 * It lives in the header rather than on the timesheet page because a clock you
 * have to navigate to is a clock people forget to stop. The header is also the
 * only place that can show a timer is STILL RUNNING while you are reading a
 * report somewhere else - which is the whole reason a running entry is worth
 * rendering at all.
 *
 * The elapsed figure is counted in the browser from the entry's own `begin`, so it
 * ticks without a request per second; the query behind it only has to notice a
 * clock started or stopped somewhere else.
 */
export function TimerMenu() {
  const { data: running } = useRunningEntry();
  const { data: projects } = useProjects();
  const { data: activities } = useActivities();
  const start = useStartEntry();
  const stop = useStopEntry();

  const [open, setOpen] = React.useState(false);
  const [projectId, setProjectId] = React.useState<string | null>(null);
  const [activityId, setActivityId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Re-renders once a second only while something is actually running.
  const [, tick] = React.useState(0);
  React.useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  const elapsed = running
    ? Math.max(0, Math.floor((Date.now() - new Date(running.begin).getTime()) / 1000))
    : 0;

  const projectOptions = React.useMemo(
    () => (projects ?? []).map((p) => ({ value: p.id, label: p.name, description: p.customer.name })),
    [projects],
  );

  const onStart = async () => {
    setError(null);
    if (!projectId || !activityId) {
      setError('Choose a project and an activity before starting the clock.');
      return;
    }
    try {
      await start.mutateAsync({ projectId, activityId });
      setOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the clock.');
    }
  };

  const onStop = async () => {
    if (!running) return;
    try {
      const result = await stop.mutateAsync(running.id);
      // A toast, because stopping now happens from any page - there is no longer
      // one screen guaranteed to be on-screen to report it.
      toast(
        result.recorded
          ? `Recorded ${formatDuration(result.durationSeconds ?? 0)}.`
          : result.message ?? 'Nothing was recorded.',
      );
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not stop the clock.');
    }
  };

  if (running) {
    return (
      <div className="flex min-w-0 items-center gap-2 rounded-md border border-primary/40 bg-primary/5 py-1 pl-2.5 pr-1">
        <span className="size-2 shrink-0 animate-pulse rounded-full bg-primary" aria-hidden />
        {/* The project is the first thing to go on a narrow screen: the elapsed
            figure and the way to stop it are what the header must always keep. */}
        <span className="hidden min-w-0 text-sm sm:block">
          <span className="block truncate font-medium leading-tight">{running.activity.name}</span>
          <span className="block truncate text-xs leading-tight text-muted-foreground">
            {running.project.name}
          </span>
        </span>
        <span className="ts-num text-sm font-semibold tabular-nums" aria-label="Time elapsed">
          {hms(elapsed)}
        </span>
        <Button hint={"Stop the timer and book the time"}
          size="sm"
          variant="destructive"
          className="h-7 gap-1.5"
          onClick={() => void onStop()}
          disabled={stop.isPending}
        >
          <Square className="size-3.5" />
          <span className="hidden sm:inline">{stop.isPending ? 'Stopping…' : 'Stop'}</span>
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button hint={"Start a timer — the time is booked when you stop it"} variant="outline" size="sm" className="gap-1.5">
          <Timer className="size-4" />
          <span className="hidden sm:inline">Start timer</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium">Start the clock</p>
          <p className="text-xs text-muted-foreground">
            It keeps running while you work elsewhere. Stopping it records the time.
          </p>
        </div>

        <Combobox
          value={projectId}
          onChange={(v) => {
            setProjectId(v);
            // A project-specific activity may not survive the change.
            const stillValid = (activities ?? []).some(
              (a) => a.id === activityId && (fitsProject(a, v, projects)),
            );
            if (!stillValid) setActivityId(null);
          }}
          options={projectOptions}
          placeholder="Project"
          searchPlaceholder="Search projects"
        />

        <Combobox
          value={activityId}
          onChange={setActivityId}
          options={(activities ?? [])
            .filter((a) => fitsProject(a, projectId, projects))
            .map((a) => ({
              value: a.id,
              label: a.name,
              hint: a.project ? undefined : 'any project',
            }))}
          placeholder={projectId ? 'Activity' : 'Project first'}
          searchPlaceholder="Search activities"
          disabled={!projectId}
        />

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button hint={"Start the timer for this project and activity"} className="w-full" onClick={() => void onStart()} disabled={start.isPending}>
          <Play className="size-4" />
          {start.isPending ? 'Starting…' : 'Start'}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/** h:mm:ss while running - seconds matter on a clock somebody is watching. */
function hms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
